// hooks/useStrategyLogs.js
import { useState, useEffect, useCallback, useRef } from "react";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─── Shared fetch helper ───────────────────────────────
const fetchJson = async (url, signal) => {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (!json.ok) throw new Error(json.error || "Unknown error");
  return json;
};

// ─── 1. Filtered logs list ─────────────────────────────
// Usage: const { logs, loading, error, refetch } = useStrategyLogs({ date, symbol, event, level })
export function useStrategyLogs(filters = {}, { autoRefreshMs = 0 } = {}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);

  // Serialize filters so useCallback deps stay stable
  const filtersKey = JSON.stringify(filters);

  const fetchLogs = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
          if (v) params.set(k, v);
        });

        const json = await fetchJson(
          `${BASE_URL}/api/strategy/logs?${params}`,
          signal,
        );
        setLogs(json.logs || []);
        setCount(json.count || 0);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [filtersKey],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    fetchLogs(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchLogs]);

  // Optional auto-refresh
  useEffect(() => {
    if (!autoRefreshMs) return;
    const t = setInterval(() => fetchLogs(), autoRefreshMs);
    return () => clearInterval(t);
  }, [fetchLogs, autoRefreshMs]);

  return { logs, count, loading, error, refetch: () => fetchLogs() };
}

// ─── 2. Daily summary (counts by event / symbol / level) ─
// Usage: const { summary, loading, error, refetch } = useStrategyLogsSummary({ date })
export function useStrategyLogsSummary(
  { date, strategy = "regmom" } = {},
  { autoRefreshMs = 0 } = {},
) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ strategy });
        if (date) params.set("date", date);

        const json = await fetchJson(
          `${BASE_URL}/api/strategy/summary?${params}`,
          signal,
        );
        setSummary(json);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [date, strategy],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    fetchSummary(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchSummary]);

  useEffect(() => {
    if (!autoRefreshMs) return;
    const t = setInterval(() => fetchSummary(), autoRefreshMs);
    return () => clearInterval(t);
  }, [fetchSummary, autoRefreshMs]);

  return { summary, loading, error, refetch: () => fetchSummary() };
}

// ─── 3. Single symbol journey ──────────────────────────
// Usage: const { timeline, loading, error, refetch } = useSymbolJourney("RELIANCE", { date })
export function useSymbolJourney(symbol, { date, strategy = "regmom" } = {}) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(!!symbol);
  const [error, setError] = useState(null);

  const fetchJourney = useCallback(
    async (signal) => {
      if (!symbol) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ strategy });
        if (date) params.set("date", date);

        const json = await fetchJson(
          `${BASE_URL}/api/strategy/${encodeURIComponent(symbol)}?${params}`,
          signal,
        );
        setTimeline(json.timeline || []);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [symbol, date, strategy],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    fetchJourney(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchJourney]);

  return { timeline, loading, error, refetch: () => fetchJourney() };
}
